/** Issuing a filing, the filings already issued and closing the period (moved out of `Reports.tsx`
 * unchanged; they are the Issue tab now).
 */

import { ClockCounterClockwise, Fingerprint } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { apiUrl } from "../api/client";
import { useMe } from "../api/auth";
import { usePeriodLock } from "../api/periodLock";
import { usePdfCapability } from "../api/pdfCapability";
import { useReader } from "../api/profile";
import type { Scope2Method } from "../api/queries";
import {
  useArtifacts,
  useClosePeriod,
  useIssueArtifact,
  useReconciliation,
  useRederivation,
  verificationPackUrl,
} from "../api/queries";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { Empty, Panel } from "../components/Panel";
import { instantDate } from "../lib/format";
import { FilingPdf } from "./FilingPdf";
import { useIssueSnapshot } from "./filings/issueSnapshotWords";
import { IssueConfirmDrawer, IssueSnapshot } from "./filings/IssueSnapshot";
import { PeriodLockState } from "./PeriodLock";
import { figuresLine, sourcePeriodsLine } from "./periodCloseModel";
import { VerdictGlyph } from "./ReportsFindingRow";
import { issuedFiling } from "./verdictWords";
import styles from "./Screen.module.css";
import own from "./Reports.module.css";
import { amount, cls } from "./reportsFormat";
import { keepSearch } from "./search";
import type { ScreenSearch } from "./search";

/** Filings issued from this obligation — **ADR-0020**.
 *
 * **The distinction this panel exists to hold: a report is not a filing.** Everything above is
 * rebuilt out of today's figures every time the screen opens, which is right for a preview and is
 * not evidence of anything. A filing is frozen, hashed and pinned to a ledger state, and it is what
 * "the figures as filed on 10 November" means.
 *
 * The digest is on screen rather than behind a detail view, because ADR-0020's consequence is that
 * the answer to *"give me my filing"* is a readable document **plus** a hash — and a hash nobody is
 * shown is a hash nobody checks.
 */
export function Filings({
  obligationId,
  method,
  entityName,
}: {
  obligationId: string;
  method: Scope2Method;
  entityName: string;
}) {
  const reader = useReader();
  const { data: me } = useMe();
  /** The read, not `data = []`. A failed one rendered as *"nothing has been filed for this
   * obligation yet"* — a statement about the company's filing history, produced by a dropped
   * request, on the panel somebody checks before issuing another (EXP-29). */
  const artifactsRead = useArtifacts(obligationId);
  const { data: artifacts = [], isPending } = artifactsRead;
  /** Asked once there is a filing to print, and **read, not probed**: the PDF link answers a named
   * 503 on a deployment with no renderer, and a control that predictably fails is UX-04's defect.
   * Firing the request to find out would fail once per reader and put a 503 in the logs for a page
   * view. */
  const pdfCapability = usePdfCapability(artifacts.length > 0);
  const issue = useIssueArtifact(obligationId, method);
  const snapshot = useIssueSnapshot(obligationId, method);
  const [confirming, setConfirming] = useState(false);
  const [checking, setChecking] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);
  const check = useRederivation(checking ?? undefined, Boolean(checking));

  // Issuing is an Approver's act (ADR-0008). A Contributor is told whose decision it is rather
  // than shown a button that predictably 403s.
  const mayIssue = me?.role === "approver" || me?.role === "admin";

  /** Whether the last press was answered as a replay rather than writing a filing (REP-06).
   *
   * Held rather than derived from `issue.data`, which survives the next render and would keep
   * saying "already filed" after a later press that did file. Cleared at the start of every
   * attempt, like the refusal beside it. */
  const [replayed, setReplayed] = useState(false);

  async function onIssue() {
    setRefusal(null);
    setReplayed(false);
    try {
      // **One key per attempt, sent by `useIssueArtifact`.** A double press, or a press after a
      // timeout, reuses it and is answered with the filing it already produced rather than
      // minting a second — which is what `Idempotency-Key` is for and what this screen was not
      // asking for.
      const { replayed: again } = await issue.mutateAsync(method);
      setReplayed(again);
    } catch (error) {
      // **Issuing is the write where an unknown outcome matters most.** An artifact is written
      // once and never corrected.
      //
      // **And the key does not reach across a reload.** It lives in a ref, so pressing the button
      // again in this tab is safe and pressing it after reloading the page is a second filing —
      // two rows, two ledger entries, two digests of the same figures. That is why the sentence
      // below still says to look at the list first, and why `retrySafe` is withheld: it would
      // print *"sending it again is safe either way"*, which is false in precisely the case the
      // advice beside it sends the reader into.
      setRefusal(error);
    }
  }

  return (
    <Panel
      title="Filings issued"
      // **"None yet" is a claim about the company's filing history**, so it is made only by a read
      // that answered with an empty list. A failed read says nothing here (the body names the
      // failure), and a read in flight says nothing either (whole-product review, finding 12).
      sub={
        artifactsRead.error || isPending
          ? undefined
          : artifacts.length
            ? `${artifacts.length} for ${entityName}`
            : "none yet"
      }
      column="1 / 13"
    >
      {/* **ASR-03.** Beside the filings, because the pack is what somebody outside checks a filing
          with — and a download that lived on its own screen is one an assurance provider is told
          about rather than finds. A link rather than a button: it is a file the browser saves. */}
      <p className={own.pack}>
        <a
          className={own.packLink}
          href={verificationPackUrl(obligationId, method)}
          data-testid="verification-pack"
        >
          Download the verification pack
        </a>
        {" "}
        The figures, their factors, the hash chain and an integrity manifest, with instructions
        for checking all of it <strong>without EmitIQ</strong>. No document bytes: evidence is held
        under a short-lived URL and a per-organisation key, so the pack carries each document&apos;s
        digest instead.
      </p>

      {artifactsRead.error ? (
        <ReadFailed
          of="The filings issued for this obligation"
          error={artifactsRead.error}
          onRetry={() => void artifactsRead.refetch()}
        />
      ) : isPending ? (
        <Empty>Loading…</Empty>
      ) : artifacts.length === 0 ? (
        <Empty>Nothing has been filed for this obligation yet.</Empty>
      ) : (
        <ul className={own.filings}>
          {artifacts.map((filing) => (
            <li key={filing.id} className={own.filing}>
              <span className={own.filingMain}>
                <span className={own.filingTitle}>
                  <span className={cls(own.filingFigure, "num")}>
                    {amount(filing.stated_total_tonnes, "tCO2e")}
                  </span>{" "}
                  tCO₂e
                  <span className={own.filingWhen}>
                    {instantDate(filing.issued_at, reader)} · {filing.scope2_method}-based ·
                    ledger #{filing.ledger_seq}
                  </span>
                </span>
                {/* **Which issue this is** (gate 24). Derived by the server from the ledger order and
                    stored nowhere. It says a later version was *issued here* - which one, if any,
                    was submitted to a regulator is not something this product records, and the
                    sentence says so rather than letting "superseded" read as a legal fact. */}
                <span className={own.filingVersion} data-testid="filing-version">
                  Version {filing.version} of {filing.of_versions}
                  {filing.superseded_by
                    ? " · a later version was issued here. Which version was submitted to the regulator is not recorded in EmitIQ."
                    : filing.of_versions > 1
                      ? " · the latest issued."
                      : ""}
                </span>
                {/* The verdict **at the moment of issue**, not today's. A filing that showed a
                    current verdict would be a filing whose checks appear to change after it was
                    made, which is the opposite of what freezing one is for. */}
                <span className={own.filingVerdict} data-testid="filing-issued-standing">
                  <VerdictGlyph verdict="issued" />
                  {issuedFiling(filing).headline}
                  <span className={own.filingWhen} data-testid="filing-at-issue">
                    {" "}
                    · {issuedFiling(filing).detail}
                  </span>
                </span>
                {/* **The fingerprint of the JSON record, not of the page `Open` shows.** The page
                    prints the same 64 characters and says how to check them; this is the copy a
                    person sends *separately*, which is the only kind that means anything. */}
                <span className={own.digest} data-testid="filing-digest">
                  {filing.sha256}
                </span>
              </span>
              <span className={own.filingActions}>
                <a
                  className={styles.fixLink}
                  href={apiUrl(`/api/v1/artifacts/${filing.id}/view`)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="filing-open"
                >
                  Open ›
                </a>
                <a
                  className={styles.fixLink}
                  href={apiUrl(`/api/v1/artifacts/${filing.id}`)}
                  download
                  data-testid="filing-json"
                >
                  JSON
                </a>
                {/* **A table of the same record** (gate 24): every line a row, an unsupported one
                    with its reason, the record's digest on the first rows. Drawn on request from
                    the stored artifact like the page and the PDF - a view, never the record, and the
                    file says so inside it. */}
                <a
                  className={styles.fixLink}
                  href={apiUrl(`/api/v1/artifacts/${filing.id}/csv`)}
                  download
                  data-testid="filing-csv"
                >
                  CSV
                </a>
                {/* **The same table as a workbook** (ADR-0161): the CSV's rows plus a sheet saying it
                    is a view and giving the record's digest, and a number column only where a
                    spreadsheet number holds the figure exactly. Same route family, same refusal of a
                    record that no longer hashes to its digest. */}
                <a
                  className={styles.fixLink}
                  href={apiUrl(`/api/v1/artifacts/${filing.id}/xlsx`)}
                  download
                  data-testid="filing-xlsx"
                >
                  XLSX
                </a>
                {/* **A third view of the same record** (ADR-0078): drawn on request from the stored
                    artifact, never stored, hashed or ledgered, with the fingerprint on every page.
                    New tab, because a failed render answers with a named refusal and a reader
                    should not lose this screen to it.

                    **One of three things, decided by a read of the deployment (S24)**: the link
                    when a renderer is configured; a brief marker while that is being asked; and
                    nothing here when it is not - the sentence saying so is printed once, below the
                    list, rather than once per filing. Never a disabled button and never a link
                    that predictably 503s. When the check itself failed, neither answer is claimed
                    (the failure is shown below with a way to retry). */}
                {pdfCapability.data?.available ? (
                  <FilingPdf filingId={filing.id} />
                ) : pdfCapability.isPending ? (
                  <span className={own.fine} data-testid="filing-pdf-checking">
                    PDF…
                  </span>
                ) : null}
                <button
                  type="button"
                  className={styles.fixLink}
                  onClick={() => setChecking(checking === filing.id ? null : filing.id)}
                >
                  {checking === filing.id ? "Hide check" : "Still true?"}
                </button>
              </span>

              {checking === filing.id ? (
                <span className={own.rederivation}>
                  {check.isPending ? (
                    "Rebuilding this filing from today's data…"
                  ) : check.error ? (
                    /* **A check that could not be run says so, with a way to run it again.** The
                       old sentence was the same for a dropped request and for a filing that
                       answered nothing, and the first of those is the one a reader can fix. */
                    <ReadFailed
                      of="This filing's re-derivation"
                      error={check.error}
                      onRetry={() => void check.refetch()}
                    />
                  ) : !check.data ? (
                    "That check could not be run."
                  ) : (
                    <>
                      {/* **Two answers, never one badge.** `stored_intact` is the record's own
                          integrity and a failure there is serious; `matches_today` false is an
                          ordinary restatement. Collapsing them would make a corrected figure look
                          like tampering, and an alarm that cries wolf is one people turn off. So
                          they are two rows with two different tones: the first goes red when it
                          fails, the second never does. */}
                      <span className={own.checks} data-testid="rederivation">
                        <span
                          className={own.checkRow}
                          data-testid="check-stored"
                          data-ok={String(check.data.stored_intact)}
                        >
                          <span className={own.checkQuestion}>
                            <Fingerprint size={14} aria-hidden="true" />
                            The stored record
                          </span>
                          <span
                            className={cls(
                              own.checkAnswer,
                              check.data.stored_intact ? own.checkOk : own.checkBad,
                            )}
                          >
                            {check.data.stored_intact
                              ? "Intact"
                              : "No longer matches its own digest"}
                          </span>
                        </span>
                        <span
                          className={own.checkRow}
                          data-testid="check-today"
                          data-ok={String(check.data.matches_today)}
                        >
                          <span className={own.checkQuestion}>
                            <ClockCounterClockwise size={14} aria-hidden="true" />
                            Against today&apos;s data
                          </span>
                          <span
                            className={cls(
                              own.checkAnswer,
                              check.data.matches_today ? own.checkOk : own.checkMoved,
                            )}
                          >
                            {check.data.matches_today
                              ? "Reproduces exactly"
                              : "Superseded by later figures"}
                          </span>
                        </span>
                      </span>
                      <span className={own.checkWhy}>{check.data.explanation}</span>
                    </>
                  )}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/* **The other branch of the PDF link (S24): the server's sentence, printed as it stands.**
          "Not available on this deployment" is true; "not built" is not - the renderer exists
          behind a port, with tests, and what is absent here is a deployed function. A reader told
          the feature does not exist does not ask for it to be turned on, so this renders the
          server's wording (`PDF_UNAVAILABLE_MESSAGE`) and adds none of its own claim. Printed once,
          not per filing, and only beside filings: with none issued there is nothing to print. */}
      {artifacts.length > 0 && pdfCapability.data?.available === false ? (
        <p className={own.fine} data-testid="pdf-unavailable">
          {pdfCapability.data.message}
        </p>
      ) : null}
      {/* **What a PDF is, said where it is offered.** Only when it can be offered: a note about a
          control that is not there would be a second way to say the sentence above. */}
      {artifacts.length > 0 && pdfCapability.data?.available ? (
        <p className={own.fine} data-testid="pdf-note">
          A PDF is a printed view of an issued filing&apos;s stored record, drawn when you ask for it
          and not kept. The record&apos;s fingerprint is printed at the foot of every page so the paper
          can be checked against it; the PDF is not the record, and no PDF of this filing is
          recorded or hashed. Only issued filings can be printed.
        </p>
      ) : null}
      {artifacts.length > 0 && pdfCapability.error ? (
        <ReadFailed
          of="Whether PDF export is available"
          error={pdfCapability.error}
          onRetry={() => void pdfCapability.refetch()}
        />
      ) : null}

      {/* **What the code under each filing is for, said where it is shown.** The hand-off is a
          finance director or an accountant putting these figures into a directors' report or a
          regulator's own channel, and a 64-character string with no sentence beside it is one
          nobody checks. Three claims, each held down by the same wording on the filing's own
          page (`regimes/render.py`, `NOT_PROVED`): it is the fingerprint of the JSON and not of
          the page; send it separately; a match shows the record is unchanged, not that it is right
          or may be filed. EmitIQ submits nothing — the company does. */}
      {artifacts.length > 0 ? (
        <p className={own.fine} data-testid="fingerprint-note">
          The code under each filing is the fingerprint of its <strong>JSON record</strong>, not of
          the page that <em>Open</em> shows, and not of a <em>PDF</em>, which prints the same code on
          every page and is a view of the record too. Whoever receives a copy checks it by hashing
          the JSON; the page walks them through it. Send them the code <strong>in a separate message</strong>{" "}
          from the file, because a code that travels with a document can be changed with it. A
          match shows the record is unchanged since it was issued, not that the figures are right,
          or that the report may be filed. EmitIQ does not submit anything: you do.
        </p>
      ) : null}

      <p className={own.fine}>
        Everything in the disclosures is a <strong>preview</strong>, rebuilt from today&apos;s
        figures each time this screen opens. Issuing freezes it: the canonical JSON becomes the
        record, it is hashed into the ledger, and it stops changing when the figures behind it do.
      </p>

      {/* **What the press will freeze, said before it** (design review 2026-11-03): the verdict, the
          blocking checks, whether it is approved and the stated total. It states and does not gate;
          issuing is still a person's judgement (ADR-0020). */}
      {mayIssue ? <IssueSnapshot parts={snapshot} /> : null}

      <p className={own.issueRow}>
        {mayIssue ? (
          <button
            type="button"
            className={styles.primary}
            disabled={issue.isPending}
            onClick={() => setConfirming(true)}
          >
            {issue.isPending ? "Issuing…" : `Issue this filing (${method}-based)`}
          </button>
        ) : (
          <span className={own.fine}>
            An <strong>Approver</strong> issues a filing. Preparing one and accepting it are
            separate acts, and this is the most consequential acceptance in the product.
          </span>
        )}
      </p>

      {/* Issuing is **not** gated on the verdict, and the screen says why rather than leaving a
          reader to wonder whether the product approved of what they just did. */}
      <p className={own.fine}>
        A filing can be issued whatever the checks say: whether a report may lawfully be filed is
        your judgement, not EmitIQ&apos;s. The verdict travels inside the artifact, so what was
        outstanding at the time stays on the record.
      </p>

      {/* **A press the server answered rather than acted on** (REP-06).
          The second press of a double press, or a press after a timeout that had already filed,
          returns the filing it already made — `200` rather than `201`. Silence would read as
          "that is now filed", and a reader who pressed twice would believe they had two. Said
          here, where the button is, and only until the next attempt. */}
      {replayed ? (
        <p className={own.fine} role="status" data-testid="issue-replayed">
          That attempt had already issued this filing, so nothing was written a second time. The
          one in the list above is it.
        </p>
      ) : null}

      <WriteFailed
        error={refusal}
        of="the filings above"
        onReload={() => {
          void artifactsRead.refetch();
          setRefusal(null);
        }}
      />

      <IssueConfirmDrawer
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          void onIssue().finally(() => setConfirming(false));
        }}
        parts={snapshot}
        pending={issue.isPending}
        method={method}
      />
    </Panel>
  );
}

/** Closing the period — **CLS-01**.
 *
 * Placed above the filings panel because that is the order the work runs in: reconcile, close,
 * issue. A close that lived on its own screen would be a step people discover after issuing.
 *
 * **The blockers are the panel.** "Not ready" with a count is a sentence nobody can act on, and
 * each blocker here is a different place to go: a census gap is a document to ask somebody for, a
 * document in review is one to look at, an open approval is a person to chase. A single readiness
 * bar would send all three to the same place.
 *
 * **Closing now blocks** (ADR-0094). A write that reaches a closed period is refused with
 * `period_closed`, and the way back is on this panel: an Admin reopens it, with a reason (and, when a
 * filing was issued, a typed acknowledgement that it stays as filed). The state, the history and the
 * reopen are `PeriodLockState`; the sentence below the blockers is the server's, once.
 */
export function ClosePeriod({ periodId, canClose }: { periodId: string | undefined; canClose: boolean }) {
  const reconciliationRead = useReconciliation(periodId);
  const { data: reconciliation, isPending } = reconciliationRead;
  const close = useClosePeriod(periodId);
  const lockRead = usePeriodLock(periodId);
  const [refusal, setRefusal] = useState<unknown>(null);

  if (!periodId) return null;

  const closed = reconciliation?.status === "locked";
  const estimated =
    reconciliation?.methods.find((m) => m.method === "estimated")?.figures ?? 0;

  return (
    <Panel
      title="Closing this period"
      sub={closed ? "Closed" : reconciliation ? reconciliation.sentence : undefined}
      column="1 / 13"
    >
      {/* **A failed read is not a period still loading.** `isPending || !reconciliation` covered
          both, so a dropped request left this panel saying "Loading…" for ever — and the blockers
          it exists to list are what a reader is here for (EXP-29). */}
      {reconciliationRead.error ? (
        <ReadFailed
          of="This period's reconciliation"
          error={reconciliationRead.error}
          onRetry={() => void reconciliationRead.refetch()}
        />
      ) : isPending || !reconciliation ? (
        <Empty>Loading…</Empty>
      ) : (
        <div data-testid="reconciliation">
          {reconciliation.blockers.length > 0 ? (
            <ul className={own.blockers}>
              {reconciliation.blockers.map((blocker) => (
                <li key={blocker.code} className={own.blocker} data-blocker={blocker.code}>
                  <span className={own.blockerWhat}>{blocker.message}</span>
                  <span className={own.blockerRemedy}>{blocker.remedy}</span>
                  {/* **The place to go and do it** (journey walk, 2026-10-31): "1 approval is still
                      open" named no approval. The server names the record the longest-waiting
                      request is on; its Workflow tab is where the decision is. */}
                  {blocker.record_id ? (
                    <Link
                      className={styles.fixLink}
                      to="/records/$recordId"
                      params={{ recordId: blocker.record_id }}
                      search={(prev: Partial<ScreenSearch>) => ({ ...keepSearch(prev), tab: "workflow" })}
                      data-testid="blocker-record-link"
                    >
                      {blocker.count === 1 ? "Open the approval ›" : "Open the longest-waiting approval ›"}
                    </Link>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className={own.fine}>{reconciliation.sentence}</p>
          )}

          {/* Stated, never blocking. An estimate with a stated basis is a legitimate figure under
              the GHG Protocol and refusing it would be EmitIQ inventing a rule — but closing over
              an inventory that is a quarter estimated is a decision to make knowingly. */}
          <p className={own.statline}>
            {figuresLine(reconciliation)} · {sourcePeriodsLine(reconciliation)}
            {estimated > 0 ? ` · ${estimated} estimated` : ""}
            {reconciliation.declared_nils > 0
              ? ` · ${reconciliation.declared_nils} confirmed none`
              : ""}
          </p>

          {canClose && !closed ? (
            <button
              type="button"
              // Secondary: the one primary on this screen is issuing a filing.
              className={styles.secondary}
              disabled={!reconciliation.can_close || close.isPending}
              aria-describedby={reconciliation.can_close ? undefined : "close-unavailable-why"}
              onClick={() => {
                setRefusal(null);
                close.mutate(
                  { expectedClosureSeq: lockRead.data?.closure_seq },
                  { onError: (error: unknown) => setRefusal(error) },
                );
              }}
            >
              {close.isPending ? "Closing…" : "Close this period"}
            </button>
          ) : null}
          {/* **A disabled control owes a reason** (design rule 6), in text a screen reader reads
              with the button: the blockers above are the reason, and a button that is merely
              greyed makes a reader hunt for which one. Counted from the same list, never
              asserted separately, so it cannot disagree with it. */}
          {canClose && !closed && !reconciliation.can_close ? (
            <p id="close-unavailable-why" className={own.fine} data-testid="close-unavailable-why">
              {reconciliation.blockers.length > 0
                ? `Closing is unavailable until ${
                    reconciliation.blockers.length === 1
                      ? "the item"
                      : `the ${reconciliation.blockers.length} items`
                  } listed above ${reconciliation.blockers.length === 1 ? "is" : "are"} cleared.`
                : "Closing is unavailable: the reconciliation says this period cannot be closed yet."}
            </p>
          ) : null}
          {!canClose && !closed ? (
            /* Told whose decision it is, rather than shown a button that predictably 403s. */
            <p className={own.fine}>An <strong>Approver</strong> closes a period.</p>
          ) : null}
          {/* **A 409 carries the whole reconciliation**, so the blockers above refresh rather than
              a sentence naming one of them — which is why the reload is the remedy here. */}
          <WriteFailed
            error={refusal}
            of="this period's blockers"
            onReload={() => {
              void reconciliationRead.refetch();
              void lockRead.refetch();
              setRefusal(null);
            }}
          />

          <PeriodLockState
            periodId={periodId}
            onReload={() => void reconciliationRead.refetch()}
          />

          {/* The server's own sentence about what closing does (ADR-0094), written once. The field
              keeps its old name, `locking_is_not_built`, which no longer describes it. */}
          <p className={own.notice}>
            <span className={own.noticeTitle}>What closing does</span>
            {reconciliation.locking_is_not_built}
          </p>
        </div>
      )}
    </Panel>
  );
}
